import { MAX_BLOCKS_PER_ACCOUNT } from "@townsy/contracts";
import { deleteBlock, fetchMyBlocks, postBlock } from "../api/safety";
import { STORAGE_KEYS, readJson, writeJson } from "../lib/storage";
import { useTownStore } from "../state/store";

/**
 * Muting people (ADR-0013; docs/06 "recipient block"): their chat lines and speech bubbles are hidden for you. An
 * account keeps its mutes on the server (`/me/blocks`, every device); a guest's mutes stay in this browser only.
 */

const ACTOR_ID_RE = /^act_[a-z0-9]{20,32}$/;

export function readGuestMutes(): string[] {
  const raw = readJson<unknown>("local", STORAGE_KEYS.mutedActors);
  return Array.isArray(raw) ? raw.filter((x): x is string => typeof x === "string" && ACTOR_ID_RE.test(x)).slice(0, MAX_BLOCKS_PER_ACCOUNT) : [];
}

function writeGuestMutes(ids: string[]): void {
  writeJson("local", STORAGE_KEYS.mutedActors, ids.slice(-MAX_BLOCKS_PER_ACCOUNT));
}

/** Load the mutes for the current identity (after bootstrap): the account's from the server, else this browser's. */
export async function loadMutes(isAccount: boolean): Promise<void> {
  const S = useTownStore.getState();
  if (!isAccount) {
    S.setMutes(Object.fromEntries(readGuestMutes().map((id) => [id, null])));
    return;
  }
  try {
    const res = await fetchMyBlocks();
    useTownStore.getState().setMutes(Object.fromEntries(res.items.map((b) => [b.actorId, b.blockId])));
  } catch {
    /* Mutes are a convenience: the town works without them; the next load retries. */
  }
}

export async function muteActor(actorId: string, isAccount: boolean): Promise<void> {
  if (!isAccount) {
    writeGuestMutes([...readGuestMutes().filter((x) => x !== actorId), actorId]);
    useTownStore.getState().setMuted(actorId, true, null);
    return;
  }
  const block = await postBlock(actorId);
  useTownStore.getState().setMuted(actorId, true, block.blockId);
}

export async function unmuteActor(actorId: string, isAccount: boolean): Promise<void> {
  const blockId = useTownStore.getState().mutes[actorId] ?? null;
  if (isAccount && blockId) await deleteBlock(blockId);
  else writeGuestMutes(readGuestMutes().filter((x) => x !== actorId));
  useTownStore.getState().setMuted(actorId, false);
}
